import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectCid } from "../../../../features/companyid";
import { COMPONENT_TYPES } from "../../../../pages/constants/AiAgent";
import { useComponentAction } from "./useComponentAction";
import useComponentSearch from "./useComponentSearch";
import Button from "../../../ui/Button";
import { IoMdClose } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const IndustryChip = ({ industry, onRemove, disabled }) => (
  <div className="flex items-center gap-1.5 border border-gray-200 rounded-full px-2.5 py-1 bg-blue-50 text-sm text-blue-700">
    <span className="truncate">{industry.title}</span>
    {!disabled && (
      <button
        onClick={() => onRemove(industry.id)}
        className="text-blue-400 hover:text-red-500 flex-shrink-0"
      >
        <IoMdClose size={14} />
      </button>
    )}
  </div>
);

const IndustrySearchBar = ({ payload, disabled }) => {
  const { text, data } = payload;
  const searchEndpoint = data?.search_endpoint || "";
  const placeholder = data?.placeholder || "Search industries...";

  const companyId = useSelector(selectCid);
  const { sendAction } = useComponentAction();

  const initialSelected = () => {
    if (data?.selected_industries?.length > 0) {
      return data.selected_industries.map((item) => ({
        id: item.id,
        title: item.title || item.name || item.id,
      }));
    }
    return [];
  };

  const [selected, setSelected] = useState(initialSelected);
  const [submitted, setSubmitted] = useState(disabled);
  const wasSkipped = disabled && selected.length === 0 && data?.skipped;

  const { query, results, isLoading, handleQueryChange, clearSearch } =
    useComponentSearch(searchEndpoint, companyId);

  const handleSelect = (item) => {
    if (selected.find((s) => s.id === item.id)) return;
    setSelected((prev) => [
      ...prev,
      { id: item.id, title: item.title },
    ]);
    clearSearch();
  };

  const handleRemove = (id) => {
    setSelected((prev) => prev.filter((s) => s.id !== id));
  };

  const handleConfirm = () => {
    setSubmitted(true);
    sendAction(COMPONENT_TYPES.INDUSTRY_SEARCH, "confirm_industry", {
      industry_ids: selected.map((s) => s.id),
      industry_names: selected.map((s) => s.title),
    });
  };

  const handleSkip = () => {
    setSubmitted(true);
    sendAction(COMPONENT_TYPES.INDUSTRY_SEARCH, "skip_industry", {});
  };

  const isSelected = (id) => selected.some((s) => s.id === id);

  return (
    <div className="flex flex-col gap-3 w-full">
      {text && (
        <p className="text-sm text-gray-700 whitespace-pre-line">{text}</p>
      )}

      {wasSkipped && (
        <p className="text-xs text-gray-400 italic">Skipped</p>
      )}

      {!submitted && (
        <>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-gray-50"
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <AiOutlineLoading3Quarters className="w-4 h-4 text-gray-400 animate-spin" />
              </div>
            )}
          </div>

          {query.length >= (data?.min_query_length || 2) && !isLoading && results.length === 0 && (
            <p className="text-xs text-gray-400 px-1">No industries found</p>
          )}

          {results.length > 0 && (
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto bg-white">
              {results.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  disabled={isSelected(item.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between ${
                    isSelected(item.id)
                      ? "text-gray-400 bg-gray-50 cursor-default"
                      : "text-gray-700"
                  }`}
                >
                  <span className="truncate">{item.title}</span>
                  {isSelected(item.id) && (
                    <span className="text-xs text-blue-500 flex-shrink-0 ml-2">
                      selected
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((industry) => (
            <IndustryChip
              key={industry.id}
              industry={industry}
              onRemove={handleRemove}
              disabled={submitted}
            />
          ))}
        </div>
      )}

      {!submitted && (
        <div className="flex items-center justify-between mt-1">
          <Button
            variant="secondary"
            size="sm"
            className="!text-xs"
            onClick={handleSkip}
          >
            Skip
          </Button>
          <Button
            variant="black"
            size="sm"
            className="!text-xs"
            onClick={handleConfirm}
            disabled={selected.length === 0}
          >
            Confirm ({selected.length} selected)
          </Button>
        </div>
      )}
    </div>
  );
};

export default IndustrySearchBar;
